import axios from 'axios';
import { isCapabilityError, emitCapabilityError, getCapabilityErrorDetails } from './capabilityError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth and capability errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    // Handle 403 Capability errors
    if (isCapabilityError(error)) {
      const errorDetails = getCapabilityErrorDetails(error);
      emitCapabilityError(errorDetails);
    }
    
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  registerCompany: (data) => api.post('/auth/register-company', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

// Tenant
export const tenantAPI = {
  get: () => api.get('/tenant'),
  update: (data) => api.put('/tenant', data),
  initDemo: () => api.post('/init-demo-tenant'),
  getCapabilities: () => api.get('/tenant/capabilities'),
  activate: () => api.post('/tenant/activate'),
};

// User Management
export const usersAPI = {
  list: () => api.get('/users'),
  invite: (data) => api.post('/users/invite', data),
  setPassword: (data) => api.post('/users/set-password', data),
  updateRole: (userId, role) => api.put(`/users/${userId}/role?role=${role}`),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Superadmin
export const superadminAPI = {
  listTenants: () => api.get('/superadmin/tenants'),
  getTenant: (id) => api.get(`/superadmin/tenants/${id}`),
  activateTenant: (id) => api.put(`/superadmin/tenants/${id}/activate`),
  blockTenant: (id, reason) => api.put(`/superadmin/tenants/${id}/block?reason=${encodeURIComponent(reason)}`),
  extendTrial: (id, days) => api.put(`/superadmin/tenants/${id}/extend-trial?days=${days}`),
  resetTenant: (id) => api.post(`/superadmin/tenants/${id}/reset`),
  getStats: () => api.get('/superadmin/stats'),
  bootstrap: () => api.post('/superadmin/bootstrap'),
  // MANDO IA Control
  toggleMandoIA: (id, enable) => api.put(`/superadmin/tenants/${id}/mando-ia?enable=${enable}`),
};

// Clientes
export const clientesAPI = {
  list: () => api.get('/clientes'),
  get: (id) => api.get(`/clientes/${id}`),
  create: (data) => api.post('/clientes', data),
  update: (id, data) => api.put(`/clientes/${id}`, data),
  delete: (id) => api.delete(`/clientes/${id}`),
};

// Presupuestos
export const presupuestosAPI = {
  list: () => api.get('/presupuestos'),
  get: (id) => api.get(`/presupuestos/${id}`),
  create: (data) => api.post('/presupuestos', data),
  update: (id, data) => api.put(`/presupuestos/${id}`, data),
  updateEstado: (id, estado) => api.put(`/presupuestos/${id}/estado?estado=${estado}`),
  delete: (id) => api.delete(`/presupuestos/${id}`),
  getPDF: (id) => `${API_BASE}/presupuestos/${id}/pdf`,
  downloadPDF: async (id, filename) => {
    const response = await api.get(`/presupuestos/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || `presupuesto_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  duplicar: (id) => api.post(`/presupuestos/${id}/duplicar`),
};

// Obras
export const obrasAPI = {
  list: () => api.get('/obras'),
  get: (id) => api.get(`/obras/${id}`),
  create: (data) => api.post('/obras', data),
  update: (id, data) => api.put(`/obras/${id}`, data),
  updateEstado: (id, estado) => api.put(`/obras/${id}/estado?estado=${estado}`),
  delete: (id) => api.delete(`/obras/${id}`),
};

// Gastos
export const gastosAPI = {
  list: (obraId = null) => api.get('/gastos', { params: obraId ? { obra_id: obraId } : {} }),
  get: (id) => api.get(`/gastos/${id}`),
  create: (data) => api.post('/gastos', data),
  update: (id, data) => api.put(`/gastos/${id}`, data),
  delete: (id) => api.delete(`/gastos/${id}`),
};

// Facturas
export const facturasAPI = {
  list: () => api.get('/facturas'),
  get: (id) => api.get(`/facturas/${id}`),
  create: (data) => api.post('/facturas', data),
  update: (id, data) => api.put(`/facturas/${id}`, data),
  updateEstado: (id, estado) => api.put(`/facturas/${id}/estado?estado=${estado}`),
  delete: (id) => api.delete(`/facturas/${id}`),
  getPDF: (id) => `${API_BASE}/facturas/${id}/pdf`,
  downloadPDF: async (id, filename) => {
    const response = await api.get(`/facturas/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || `factura_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  duplicar: (id) => api.post(`/facturas/${id}/duplicar`),
};

// Export with filters
export const exportAPI = {
  clientes: (params = {}) => api.get('/export/clientes', { params, responseType: 'blob' }),
  presupuestos: (params = {}) => api.get('/export/presupuestos', { params, responseType: 'blob' }),
  obras: (params = {}) => api.get('/export/obras', { params, responseType: 'blob' }),
  facturas: (params = {}) => api.get('/export/facturas', { params, responseType: 'blob' }),
  gastos: (params = {}) => api.get('/export/gastos', { params, responseType: 'blob' }),
  completo: () => api.get('/export/informe-completo', { responseType: 'blob' }),
  
  download: async (type, params = {}, filename) => {
    const response = await api.get(`/export/${type}`, { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || `${type}_serama.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Pagos
export const pagosAPI = {
  list: () => api.get('/pagos'),
  get: (id) => api.get(`/pagos/${id}`),
  create: (data) => api.post('/pagos', data),
  update: (id, data) => api.put(`/pagos/${id}`, data),
  updateEstado: (id, estado, fechaPago = null) => {
    let url = `/pagos/${id}/estado?estado=${estado}`;
    if (fechaPago) url += `&fecha_pago=${fechaPago}`;
    return api.put(url);
  },
  delete: (id) => api.delete(`/pagos/${id}`),
};

// Incidencias
export const incidenciasAPI = {
  list: (obraId = null) => api.get('/incidencias', { params: obraId ? { obra_id: obraId } : {} }),
  get: (id) => api.get(`/incidencias/${id}`),
  create: (data) => api.post('/incidencias', data),
  update: (id, data) => api.put(`/incidencias/${id}`, data),
  delete: (id) => api.delete(`/incidencias/${id}`),
};

// Pendientes
export const pendientesAPI = {
  list: () => api.get('/pendientes'),
  create: (data) => api.post('/pendientes', data),
  update: (id, data) => api.put(`/pendientes/${id}`, data),
  completar: (id) => api.put(`/pendientes/${id}/completar`),
  delete: (id) => api.delete(`/pendientes/${id}`),
};

// Dashboard
export const dashboardAPI = {
  stats: () => api.get('/dashboard/stats'),
  obrasResumen: () => api.get('/dashboard/obras-resumen'),
  actividadReciente: () => api.get('/dashboard/actividad-reciente'),
};

// Seed Demo Data
export const seedDemoData = () => api.post('/seed-demo-data');

// Document Configuration
export const documentConfigAPI = {
  get: () => api.get('/document-config'),
  update: (data) => api.put('/document-config', data),
};

export default api;
